import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { track } from "../../../lib/analytics";
import { SIGN_TRAITS } from "../../../lib/astro";
import { SUN_READS, MOON_READS, RISING_READS } from "../../../lib/reads";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const spring = { duration: 0.5, ease: [0.34, 1.56, 0.64, 1] };

const Row = ({ label, trait, line, last }) => (
  <div className={`py-4 ${last ? "" : "border-b border-[#2A2C30]"}`}>
    <p className="font-mono-brand text-[10px] sm:text-[11px] font-bold tracking-[1.5px] text-[#737687] uppercase leading-snug">{label}</p>
    <p className="font-display font-bold text-[17px] text-[#FF4D0A] uppercase tracking-[0.04em] mt-1.5">{trait}</p>
    <p className="text-[13px] text-[#A3A4A3] leading-relaxed mt-1.5">{line}</p>
  </div>
);

const baselineRows = (chart, timeUnknown) => {
  const rows = [
    ["Drive \u00B7 How you start", SUN_READS[chart.sun.sign], SIGN_TRAITS[chart.sun.sign]],
    ["Recovery \u00B7 How you reset", MOON_READS[chart.moon.sign], SIGN_TRAITS[chart.moon.sign]],
  ];
  if (!timeUnknown) {
    rows.push(["Surface \u00B7 How you land in a room", RISING_READS[chart.rising.sign], SIGN_TRAITS[chart.rising.sign]]);
  }
  return rows;
};

export const Reveal = ({ firstName, chart, sid, timeUnknown }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/code-claims`, {
        email,
        source_id: sid,
        first_name: (firstName || "").slice(0, 50) || null,
        sun: chart?.sun?.sign || null,
        moon: chart?.moon?.sign || null,
        rising: timeUnknown ? null : chart?.rising?.sign || null,
      });
      track("source_code_claimed", { source_id: sid });
      setDone(true);
    } catch (err) {
      if (err?.response?.status === 429) toast.error("Too many tries. Give it a minute and send again.");
      else toast.error("That email didn't go through. Check it and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reveal-step">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        <p className="font-mono-brand text-[11px] font-bold tracking-[4px] text-[#F2F0EB] uppercase">Your code is ready</p>
        <h3 className="font-display font-bold text-[26px] sm:text-[30px] leading-tight text-[#F2F0EB] mt-2.5 sm:mt-3" data-testid="reveal-headline">
          {firstName}, your Source Code:
        </h3>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...spring, delay: 0.2 }}
        className="mt-6 sm:mt-8 rounded-2xl bg-[#141416] border border-[#2A2C30] p-4"
        data-testid="source-id-card"
      >
        <p className="font-mono-brand text-[11px] font-bold tracking-[1.5px] text-[#737687] uppercase">Source ID</p>
        <p className="font-mono-brand text-[13px] font-medium text-[#F2F0EB] mt-2" data-testid="source-id-value">{sid}</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...spring, delay: 0.65 }}
        className="mt-4 rounded-2xl bg-[#141416] border border-[#2A2C30] px-4 pb-1"
        data-testid="big-three-card"
      >
        <p className="font-mono-brand text-[11px] font-bold tracking-[1.5px] text-[#737687] uppercase pt-4">
          Your baseline
        </p>
        {baselineRows(chart, timeUnknown).map(([label, read, trait], i, arr) => (
          <Row key={label} label={label} trait={trait} line={read?.[1]} last={i === arr.length - 1} />
        ))}
        {timeUnknown && (
          <p className="font-mono-brand text-[10px] tracking-[1.5px] text-[#737687] uppercase pb-4">
            Time not set &middot; Add it later for the full read
          </p>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...spring, delay: 0.9 }}
        className="mt-6 sm:mt-8"
        data-testid="capture-step"
      >
        <p className="font-display font-semibold text-[#F2F0EB]">Your code, your read and your invite to the app. One signup.</p>
        {done ? (
          <p className="mt-4 font-mono-brand text-sm text-[#FF4D0A]" data-testid="claim-success-message">
            &#10003; You're in. Check your inbox, your code, your read and your invite to the app are on their way.
          </p>
        ) : (
          <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-3" data-testid="claim-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 rounded-xl bg-[#1B1C1F] border border-[#2A2C30] px-4 py-3 text-sm text-[#F2F0EB] placeholder:text-[#737687] caret-[#FF4D0A] focus:outline-none focus:border-[#F2F0EB]/40 transition-colors"
              data-testid="claim-email-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="rounded-xl bg-[#FF4D0A] text-[#0B0B0C] font-display font-bold text-[13px] sm:text-sm tracking-wide uppercase px-6 py-3.5 sm:py-3 hover:brightness-110 transition disabled:opacity-60"
              data-testid="claim-submit-button"
            >
              {loading ? "Sending\u2026" : "Claim"}
            </button>
          </form>
        )}
        <p className="mt-3 text-xs text-[#737687]" data-testid="claim-consent-microcopy">
          One email, everything inside: a note from us, your code, your full read
          and your TestFlight invite. No list, no spam, unsubscribe in one tap.
        </p>
      </motion.div>
    </div>
  );
};
